import { CommonModule } from '@angular/common'
import { Component, DestroyRef, EventEmitter, Output, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import {
  FormArray,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'

import { TooltipModule } from 'primeng/tooltip'

import {
  FLX_MODAL_DATA,
  FlxButtonModule,
  FlxFormControlModule,
  FlxIconModule,
  FlxModalModule,
  FlxModalService,
} from '@flowx/angular-ui-toolkit'
import { DataModelAttributeType } from '@flowx/designer/modules/core/dictionary/data-model.dictionary'
import { FlxDataModelAutocompleteComponent } from '@flowx/designer/shared/components/data-model-autocomplete/data-model-autocomplete.component'
import { DataModelAutocompleteService } from '@flowx/designer/shared/services/data-model-autocomplete.service'

import {
  AI_DM_REFERENCE_UNRESOLVED_MESSAGE,
  AI_DM_SOURCE_LEGEND,
  AI_DM_SOURCE_MARKER,
  AI_DM_STATUS_BADGE,
  AiDmKeyRow,
  AiDmKeyRowSource,
  AiDmKeyRowStatus,
  AiDmModifyModalResult,
} from '../ai-dm-key-row.model'

type ModalItem = {
  row: AiDmKeyRow
  // Display state that can change while editing (key reclassification flips status/type).
  status: AiDmKeyRow['status']
  type: DataModelAttributeType
  form: FormGroup<{
    active: FormControl<boolean>
    key: FormControl<string>
    description: FormControl<string>
  }>
}

@Component({
  selector: 'flx-ai-dm-modify-modal',
  templateUrl: './ai-dm-modify-modal.component.html',
  styleUrls: ['./ai-dm-modify-modal.component.scss'],
  imports: [
    CommonModule,
    TooltipModule,
    FlxIconModule,
    FlxModalModule,
    FlxFormControlModule,
    FlxButtonModule,
    FormsModule,
    ReactiveFormsModule,
    FlxDataModelAutocompleteComponent,
  ],
})
export class AiDmModifyModalComponent {
  #destroyRef = inject(DestroyRef)
  #modalService = inject(FlxModalService)
  #dataModelAutocompleteService = inject(DataModelAutocompleteService)
  #data = inject(FLX_MODAL_DATA) as { rows: AiDmKeyRow[] }
  readonly #parentToChildren = new Map<string, number[]>()
  #initialSerialized = ''

  @Output() dataEventEmitter = new EventEmitter<AiDmModifyModalResult>()

  statusBadge = AI_DM_STATUS_BADGE
  sourceMarkerClass = AI_DM_SOURCE_MARKER
  legend = AI_DM_SOURCE_LEGEND
  referenceUnresolvedMessage = AI_DM_REFERENCE_UNRESOLVED_MESSAGE

  items: ModalItem[] = []
  formArray = new FormArray<ModalItem['form']>([])
  rootForm = new FormGroup({ items: this.formArray })

  constructor() {
    const orderedRows = this.#orderRowsByParent(this.#data.rows ?? [])

    this.items = orderedRows.map((row) => this.#toModalItem(row))
    this.formArray = new FormArray(this.items.map((item) => item.form))
    this.rootForm = new FormGroup({ items: this.formArray })

    this.#buildParentLookup()
    this.items.forEach((item, index) => this.#setupFormWatchers(index, item))

    this.#initialSerialized = JSON.stringify(this.formArray.getRawValue())
  }

  canSave(): boolean {
    const hasErrors = !this.formArray.valid
    const hasActiveItems = this.formArray.controls.some((ctrl) => ctrl.controls.active.value)
    // The modal is edit-only; persistence is the caller's Keep button, so an unchanged form is a no-op.
    const hasChanges = this.#initialSerialized !== JSON.stringify(this.formArray.getRawValue())
    // Block save while any active row is an unresolved reference.
    const hasUnresolvedActive = this.items.some(
      (item) => item.row.unresolved && item.form.controls.active.value
    )

    return !hasErrors && hasActiveItems && hasChanges && !hasUnresolvedActive
  }

  save(): void {
    const rows: AiDmKeyRow[] = this.items.map((item) => {
      const { active, key, description } = item.form.getRawValue()
      return {
        ...item.row,
        key,
        description,
        active,
        status: item.status,
        type: item.type,
      }
    })

    this.dataEventEmitter.emit({ confirm: true, rows })
    this.close()
  }

  close(): void {
    this.#modalService.hide()
  }

  #toModalItem(row: AiDmKeyRow): ModalItem {
    const form = new FormGroup({
      active: new FormControl(row.active, { nonNullable: true }),
      key: new FormControl(
        { value: row.key, disabled: row.keyEditable === false || !row.active },
        { nonNullable: true, validators: [Validators.required] }
      ),
      description: new FormControl(
        { value: row.description ?? '', disabled: !row.active },
        { nonNullable: true }
      ),
    })

    return { row, status: row.status, type: row.type, form }
  }

  #setupFormWatchers(index: number, item: ModalItem): void {
    const { form } = item

    form.controls.active.valueChanges
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((active) => {
        // The key path of a locked row (e.g. a project reference) is never editable.
        if (active && item.row.keyEditable !== false) {
          form.controls.key.enable()
        } else {
          form.controls.key.disable()
        }
        if (active) {
          form.controls.description.enable()
        } else {
          form.controls.description.disable()
        }

        // Cascade a parent's active state onto its direct children.
        if (!item.row.parentId) {
          const childIdx = this.#parentToChildren.get(item.row.id) ?? []
          childIdx.forEach((idx) =>
            this.formArray.at(idx).controls.active.setValue(active, { emitEvent: false })
          )
        }
      })

    form.controls.key.valueChanges
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((nextKey) => {
        // Project references must never be reclassified by a local-data-model match — that would
        // silently drop the reference. Locked rows keep their status/type.
        if (item.row.keyEditable === false || item.row.source === AiDmKeyRowSource.Project) {
          return
        }

        const match = this.#dataModelAutocompleteService
          .filterDataModel(nextKey)
          .find((result) => result.path === nextKey)

        item.type = match?.type ?? item.row.type
        item.status = match ? AiDmKeyRowStatus.Used : AiDmKeyRowStatus.Created
        form.patchValue({ description: match?.description ?? item.row.description ?? '' })
      })
  }

  #buildParentLookup(): void {
    this.items.forEach((item, idx) => {
      const parentId = item.row.parentId
      if (parentId) {
        const list = this.#parentToChildren.get(parentId) ?? []
        list.push(idx)
        this.#parentToChildren.set(parentId, list)
      }
    })
  }

  // Order so each parent is immediately followed by its direct children; childless/root rows last.
  #orderRowsByParent(rows: AiDmKeyRow[]): AiDmKeyRow[] {
    const parentIds = new Set(rows.filter((r) => r.parentId).map((r) => r.parentId))
    const rootsWithoutChildren = rows.filter((r) => !parentIds.has(r.id) && !r.parentId)

    const childrenByParent = new Map<string, AiDmKeyRow[]>()
    rows.forEach((r) => {
      if (parentIds.has(r.id)) {
        childrenByParent.set(r.id, [])
      }
    })
    rows.forEach((r) => {
      if (r.parentId && childrenByParent.has(r.parentId)) {
        childrenByParent.get(r.parentId).push(r)
      }
    })

    const byId = new Map(rows.map((r) => [r.id, r]))
    const withChildren = Array.from(childrenByParent.entries()).reduce<AiDmKeyRow[]>(
      (acc, [parentId, children]) => {
        const parent = byId.get(parentId)
        if (parent) {
          acc.push(parent)
        }
        acc.push(...children)
        return acc
      },
      []
    )

    return [...withChildren, ...rootsWithoutChildren]
  }
}
